import {
  AnchorButton,
  Callout,
  Classes,
  Collapse,
  IconSize,
  Intent,
  Spinner,
} from '@blueprintjs/core';
import classNames from 'classnames';
import * as React from 'react';
import { Dialog, Tooltip } from 'vet-bones/components';
import { Loader } from 'vet-bones/design-system/components';
import { getIntentClassName, isEqual } from 'vet-bones/utils';
import { usePortalFeatures } from 'vet-bones/utils/portal';

import { notificationsActionCreators } from 'src/actions/notifications/actionCreators';
import { TEST_IDS } from 'src/constants/testIds';
import { getLabelForIri } from 'src/containers/ProjectDiagram/utils/labels';
import { SuggestionType } from 'src/containers/ProjectMapping/types';
import { useCreateMappings } from 'src/containers/ProjectResourceSuggestionsDialog/hooks/useCreateMappings';
import { useFetchMappingSuggestions } from 'src/containers/ProjectResourceSuggestionsDialog/hooks/useFetchMappingSuggestions';
import { useGenerateMockData } from 'src/containers/ProjectResourceSuggestionsDialog/hooks/useGenerateMockData';
import { useGenerateModelAndMockData } from 'src/containers/ProjectResourceSuggestionsDialog/hooks/useGenerateModelAndMockData';
import { useInitializeDraftData } from 'src/containers/ProjectResourceSuggestionsDialog/hooks/useInitializeDraftData';
import { ProjectResourceSelectionPanel } from 'src/containers/ProjectResourceSuggestionsDialog/ProjectResourceSelectionPanel';
import {
  ProjectResourceSuggestionsData,
  ProjectResourceSuggestionsDataContext,
} from 'src/containers/ProjectResourceSuggestionsDialog/projectResourceSuggestionsData';
import { IgnoredSuggestion } from 'src/containers/ProjectResourceSuggestionsDialog/utils/getOrganizedSuggestionRecords';
import { projectSelector } from 'src/selectors/project';
import { projectLabelsByIriSelector } from 'src/selectors/projectLabelsByIri';
import { projectResourcesByIdSelector } from 'src/selectors/projectResource';
import { uiSlice } from 'src/store/ui/ui.slice';
import * as copy from 'src/templates/copy';
import { useDidValueChange } from 'src/utils/hooks/useDidValueChange';
import { useIsMounted } from 'src/utils/hooks/useIsMounted';
import { useIsVoiceboxEnabled } from 'src/utils/hooks/useIsVoiceboxEnabled';
import { useNavigate } from 'src/utils/hooks/useNavigate';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';
import { getProjectRoute } from 'src/utils/routes/getProjectRoute';
import { useEvaluateSummary } from 'src/utils/voicebox/hooks/useEvaluateSummary';

export const ProjectResourceSuggestionsDialog: React.VFC = () => {
  const dispatch = useReduxDispatch();
  const { evaluateSummary } = useEvaluateSummary();
  const navigate = useNavigate();
  const { projectId } = useRouterParams();
  const project = useReduxSelector(projectSelector(projectId));
  const labelsByIri = useReduxSelector(projectLabelsByIriSelector(projectId));
  const resourcesById = useReduxSelector(
    projectResourcesByIdSelector(projectId)
  );

  const portalFeatures = usePortalFeatures();
  const useVoiceboxSuggestions = Boolean(
    useIsVoiceboxEnabled() && portalFeatures.voiceboxSuggestions
  );

  const {
    isOpen,
    initialClassIris = [],
    initialIgnoredSuggestions = null,
    initialMappingSuggestions = null,
    initialResourceIds = [],
  } = useReduxSelector((state) => state.ui.resourceSuggestionsDialog);

  const [selectedClasses, setSelectedClasses] =
    React.useState<string[]>(initialClassIris);
  const [selectedResources, setSelectedResources] =
    React.useState<string[]>(initialResourceIds);

  const [ignoredSuggestions, setIgnoredSuggestions] = React.useState<
    IgnoredSuggestion[] | null
  >(initialIgnoredSuggestions);

  const [isGeneratingMockData, setIsGeneratingMockData] = React.useState(false);
  const [isCompleting, setIsCompleting] = React.useState(false);

  const isMounted = useIsMounted();

  const { fetchMappingSuggestions, isLoading } = useFetchMappingSuggestions();

  const generateModelAndMockData = useGenerateModelAndMockData();
  const generateMockData = useGenerateMockData();
  const initializeDraftData = useInitializeDraftData();

  const handleCloseDialog = React.useCallback(
    () =>
      dispatch(
        uiSlice.actions.toggledResourceSuggestionsDialog({
          isOpen: false,
        })
      ),
    [dispatch]
  );

  // reset initial values when dialog opens
  const isOpenChanged = useDidValueChange(isOpen);
  React.useEffect(() => {
    if (isOpenChanged && isOpen) {
      setIgnoredSuggestions(initialIgnoredSuggestions);
      setSelectedClasses(initialClassIris);
      setSelectedResources(initialResourceIds);
    }
  }, [
    initialClassIris,
    initialIgnoredSuggestions,
    initialMappingSuggestions,
    initialResourceIds,
    isOpen,
    isOpenChanged,
  ]);

  const selectedClassesChanged = useDidValueChange(selectedClasses);
  const selectedResourcesChanged = useDidValueChange(selectedResources);
  React.useEffect(() => {
    if (
      (selectedClassesChanged || selectedResourcesChanged) &&
      ignoredSuggestions &&
      (!initialIgnoredSuggestions ||
        !isEqual(selectedClasses, initialClassIris) ||
        !isEqual(selectedResources, initialResourceIds))
    ) {
      setIgnoredSuggestions(null);
    }
  }, [
    ignoredSuggestions,
    initialClassIris,
    initialIgnoredSuggestions,
    initialResourceIds,
    selectedClasses,
    selectedClassesChanged,
    selectedResources,
    selectedResourcesChanged,
  ]);

  const isAiMappingAssistDisabled = Boolean(
    project?.disableVoiceboxSuggestions
  );
  // Whether the next click should actually route through voicebox.
  // Voicebox must be globally available AND the per-project flag must not
  // have explicitly turned it off.
  const shouldUseVoicebox =
    useVoiceboxSuggestions && !isAiMappingAssistDisabled;
  const canProceed =
    !isLoading &&
    !isGeneratingMockData &&
    !isCompleting &&
    (isAiMappingAssistDisabled
      ? selectedResources.length > 0 && selectedClasses.length > 0
      : selectedResources.length > 0 || useVoiceboxSuggestions);

  const handleCreateMappings = useCreateMappings();

  const handleNextClick = React.useCallback(async () => {
    if (isLoading) {
      return;
    }

    if (
      isAiMappingAssistDisabled &&
      selectedResources.length > 0 &&
      selectedClasses.length > 0
    ) {
      const blankMappingSuggestions = selectedResources.flatMap((resourceId) =>
        selectedClasses.map((classIri) => {
          const resource = resourcesById[resourceId];
          const classLabel = getLabelForIri(labelsByIri, classIri);
          return {
            name: `${resource?.name ?? resourceId} - ${classLabel}`,
            resourceId,
            classIri,
            classLabel,
            isClassAutoCreated: false,
            isExpanded: false,
            suggestions: [],
          };
        })
      );
      await handleCreateMappings(blankMappingSuggestions, false, true);
      if (!isMounted.current) {
        return;
      }
      handleCloseDialog();
      return;
    }

    // Case 1: No resources Generate Mock Data
    if (selectedResources.length === 0 && shouldUseVoicebox) {
      setIsGeneratingMockData(true);

      initializeDraftData();

      let result: {
        success: boolean;
        error?: string;
      };
      if (selectedClasses.length === 0) {
        result = await generateModelAndMockData();
      } else {
        result = await generateMockData(selectedClasses);
      }
      if (!isMounted.current) {
        return;
      }

      if (result.success) {
        setIsCompleting(true);
      } else {
        setIsGeneratingMockData(false);
        dispatch(notificationsActionCreators.voiceboxSuggestionsFailed());
      }
      return;
    }

    // Case 2: Resources selected
    if (selectedResources.length > 0) {
      const loaded = await fetchMappingSuggestions({
        selectedClasses,
        selectedResources,
      });
      if (!isMounted.current) {
        return;
      }

      const isOneToOne =
        selectedResources.length === 1 && selectedClasses.length === 1;

      // When the user explicitly selected a single class and resource,
      // only use suggestions that match that exact pair.
      const filteredSuggestions = isOneToOne
        ? loaded.mappingSuggestions?.filter(
            (s) =>
              s.classIri === selectedClasses[0] &&
              s.resourceId === selectedResources[0]
          ) ?? null
        : loaded.mappingSuggestions;

      if (filteredSuggestions?.length) {
        await handleCreateMappings(filteredSuggestions);
        if (!isMounted.current) {
          return;
        }
        handleCloseDialog();
        if (shouldUseVoicebox) {
          navigate(
            getProjectRoute(projectId, { rescale: !selectedClasses.length })
          );
          evaluateSummary(project);
        }
      } else if (
        selectedResources.length === 1 &&
        selectedClasses.length === 1
      ) {
        const resourceId = selectedResources[0];
        const classIri = selectedClasses[0];
        const resource = resourcesById[resourceId];
        if (!resource) {
          return;
        }
        const classLabel = getLabelForIri(labelsByIri, classIri);
        const fallbackSuggestions = (loaded.rawSuggestions ?? []).filter(
          (suggestion) => {
            if (
              suggestion.type === SuggestionType.CLASS ||
              suggestion.resource.id !== resourceId
            ) {
              return false;
            }
            if (
              (suggestion.type === SuggestionType.ATTRIBUTE ||
                suggestion.type === SuggestionType.RELATIONSHIP) &&
              suggestion.classIri !== classIri
            ) {
              return false;
            }
            return true;
          }
        );
        await handleCreateMappings(
          [
            {
              name: `${resource?.name ?? resourceId} - ${classLabel}`,
              resourceId,
              classIri,
              classLabel,
              isClassAutoCreated: false,
              isExpanded: false,
              suggestions: fallbackSuggestions,
            },
          ],
          true
        );
        if (!isMounted.current) {
          return;
        }
        handleCloseDialog();
        if (shouldUseVoicebox) {
          evaluateSummary(project);
        }
      } else {
        setIgnoredSuggestions(loaded.ignoredSuggestions);
      }
    }
  }, [
    dispatch,
    evaluateSummary,
    fetchMappingSuggestions,
    generateMockData,
    generateModelAndMockData,
    handleCloseDialog,
    handleCreateMappings,
    initializeDraftData,
    isAiMappingAssistDisabled,
    isLoading,
    isMounted,
    labelsByIri,
    navigate,
    project,
    projectId,
    resourcesById,
    selectedClasses,
    selectedResources,
    shouldUseVoicebox,
  ]);

  const data: ProjectResourceSuggestionsData = {
    selectedClasses,
    selectedResources,
    setSelectedClasses,
    setSelectedResources,
  };

  const mockDataGenerationContent = (
    <div className={Classes.DIALOG_BODY}>
      <div className="sd-dialog-project-resource-suggestions__spinner">
        <Loader
          complete={isCompleting}
          onComplete={() => {
            setIsCompleting(false);
            setIsGeneratingMockData(false);
            handleCloseDialog();
            if (shouldUseVoicebox) {
              navigate(
                getProjectRoute(projectId, { rescale: !selectedClasses.length })
              );
              evaluateSummary(project);
            }
          }}
        />
        <strong>
          {
            copy.components.resourceSuggestionsDialog.generatingMockDataLoading
              .title
          }
        </strong>
        <div>
          {
            copy.components.resourceSuggestionsDialog.generatingMockDataLoading
              .subtitle
          }
        </div>
      </div>
    </div>
  );

  const selectionPanelContent = (
    <div
      className={Classes.DIALOG_BODY}
      data-testid={TEST_IDS.resourceSuggestionsDialog.container}
    >
      <ProjectResourceSelectionPanel
        disabled={isLoading || isGeneratingMockData || isCompleting}
      />
      <Collapse isOpen={Boolean(ignoredSuggestions)}>
        <Callout intent={Intent.WARNING}>
          {copy.components.resourceSuggestionsDialog.selection.noSuggestions(
            Boolean(ignoredSuggestions?.length)
          )}
        </Callout>
      </Collapse>
    </div>
  );

  const dialogContent = isGeneratingMockData
    ? mockDataGenerationContent
    : selectionPanelContent;

  return (
    <ProjectResourceSuggestionsDataContext.Provider value={data}>
      <Dialog
        autoFocus={false}
        canEscapeKeyClose={false}
        canOutsideClickClose={false}
        className={classNames(
          'sd-dialog-project-resource-suggestions',
          'sd-dialog-prompt'
        )}
        closeIconTestId={TEST_IDS.dialog.closeIcon}
        enforceFocus={false}
        isOpen={isOpen}
        lazy={false}
        onClose={handleCloseDialog}
        title={copy.components.resourceSuggestionsDialog.selection.title}
      >
        {dialogContent}
        <div className={Classes.DIALOG_FOOTER}>
          <div className={Classes.DIALOG_FOOTER_ACTIONS}>
            <AnchorButton
              data-testid={TEST_IDS.resourceSuggestionsDialog.cancelButton}
              disabled={isLoading || isGeneratingMockData || isCompleting}
              onClick={handleCloseDialog}
            >
              {copy.components.resourceSuggestionsDialog.footer.cancel}
            </AnchorButton>
            <Tooltip
              content={
                shouldUseVoicebox
                  ? copy.components.resourceSuggestionsDialog.selection.invalid
                  : copy.components.resourceSuggestionsDialog.selection
                      .invalidManual
              }
              disabled={canProceed}
            >
              <AnchorButton
                className={getIntentClassName('sd-green')}
                data-testid={TEST_IDS.resourceSuggestionsDialog.nextButton}
                disabled={!canProceed}
                icon={
                  isLoading || isGeneratingMockData || isCompleting ? (
                    <Spinner size={IconSize.STANDARD} />
                  ) : undefined
                }
                onClick={handleNextClick}
              >
                {copy.components.resourceSuggestionsDialog.footer.create}
              </AnchorButton>
            </Tooltip>
          </div>
        </div>
      </Dialog>
    </ProjectResourceSuggestionsDataContext.Provider>
  );
};
